import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ShieldCheck, Network, Scale, FileSearch,
  Landmark, Factory, Gauge, Users2, Leaf, Swords, Radar, Cpu, Globe2, AlertTriangle,
} from "lucide-react";
import { Reveal } from "../../components/pop/motion";

const DIMENSIONS = [
  { Icon: Landmark, name: "Financial Stewardship", w: "20%" },
  { Icon: Factory, name: "Operational Performance", w: "15%" },
  { Icon: Gauge, name: "Efficiency & Productivity", w: "12%" },
  { Icon: Users2, name: "Customer & Market", w: "8%" },
  { Icon: Leaf, name: "Sustainability & ESG", w: "10%" },
  { Icon: Swords, name: "Competitive Position & Moat", w: "10%" },
  { Icon: Radar, name: "Industry & Future Readiness", w: "7%" },
  { Icon: Cpu, name: "Digital, Data & AI", w: "7%" },
  { Icon: Globe2, name: "Human Capital & Footprint", w: "5%" },
  { Icon: AlertTriangle, name: "Risk, Governance & Execution", w: "6%" },
];

const PIPELINE = [
  "Where the company stands",
  "Why performance gaps exist",
  "How it compares with peers",
  "What risks & opportunities matter",
  "What value can be unlocked",
  "What management should do next",
  "How results will be tracked",
];

const AGENTS = [
  ["Orchestrator", "Plans the analysis, allocates specialists, resolves conflicts"],
  ["Company Intelligence", "Business model, segments, revenue streams, strategic priorities"],
  ["Financial Analysis", "3–5y trends, margins, cash, leverage, returns, valuation"],
  ["Operational Performance", "Industry-adaptive KPIs, bottlenecks, execution gaps"],
  ["Efficiency & Productivity", "Top-5 efficiency gaps with indicative value"],
  ["Sustainability & ESG", "Financially material E, S & G with verified ratings only"],
  ["Competitive Intelligence", "Peer matrix, SWOT, Five Forces, moat, white space"],
  ["Industry Trends", "Tailwinds, headwinds, scenarios, recommended responses"],
  ["Digital, Data & AI", "Pilots vs production AI, agentic maturity, vendor map"],
  ["Human Capital", "Frontier skills, hiring intelligence, innovation hubs"],
  ["Risk & Governance", "Board risk register with residual exposure & owners"],
  ["Value Creation", "Quantified value pools, 36-month roadmap, benefit register"],
  ["Source Attribution", "Traces every figure to its named source and grades citation quality"],
  ["Strategic Positioning", "Builds the SAMANVAI Leadership Quadrant across five comparison lenses"],
  ["Evidence Verification", "Final quality gate — every number traced to source"],
];

const GUARDRAILS = [
  ["No fabrication, ever", "Revenue, market share, ratings, partnerships and citations are never invented. Missing data is shown as 'Not publicly available — management input required'."],
  ["Confidence, displayed separately", "Every metric carries high / medium / low / estimated confidence — kept apart from the performance score so evidence quality is always visible."],
  ["Definitive scores, gaps at the end", "Every dimension carries a definitive 0–100 score. Residual data gaps and assumptions are consolidated into a single annexure at the end of the report — never scattered through the narrative."],
  ["Facts vs hypotheses", "Uploaded AI-generated reports are treated as hypotheses until verified. Every statement is classified: verified fact, calculated metric, benchmark, inference or assumption."],
  ["Credible peers only", "Regional and mid-sized companies are benchmarked against comparable peers across four groups — never defaulted against the largest global corporations."],
  ["Reproducible arithmetic", "Weighted scores are computed programmatically — dimension score × weight, visible and reproducible. Weights always total 100%."],
];

export default function CILanding() {
  return (
    <div className="min-h-screen bg-[#060d18] text-white" data-testid="ci-landing-page">
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> SAMANVAI home
          </Link>
          <Link
            to="/company-intelligence/dashboard"
            data-testid="ci-landing-enter-top-btn"
            className="inline-flex items-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] text-[12px] font-bold tracking-wide px-5 py-2 transition-colors"
          >
            Enter the suite <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
            }}
          />
          <div className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full bg-teal-400/[0.06] blur-3xl" />
          <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-amber-400/[0.05] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] font-bold uppercase text-teal-300 mb-6">
              SAMANVAI · Company Intelligence &nbsp;·&nbsp; Administrator preview
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight max-w-4xl mb-7">
              From fragmented company data to
              <span className="italic text-teal-300"> credible insight</span>, quantified value and
              <span className="italic text-amber-300"> board-level action</span>.
            </h1>
            <p className="text-lg sm:text-xl text-white/65 font-light leading-relaxed max-w-3xl mb-10">
              An agentic strategic-intelligence system that assesses any public, private, regional or multinational
              company across ten evidence-gated dimensions — and returns an Account Intelligence &amp; Enterprise
              Value-Creation Report your board can act on.
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <Link
                to="/company-intelligence/dashboard"
                data-testid="ci-landing-enter-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-teal-50 text-[#0a1220] font-bold text-sm px-7 py-3.5 transition-colors"
              >
                Enter the Intelligence Suite <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how-it-works" className="text-sm text-white/60 hover:text-teal-300 underline underline-offset-4 transition-colors">
                See how the 15-agent system works
              </a>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden border border-white/10">
              {[
                ["13", "Specialist AI agents"],
                ["10", "Scoring dimensions, weights Σ100%"],
                ["20", "Industry-adaptive KPI templates"],
                ["4", "Peer benchmark groups"],
              ].map(([n, l]) => (
                <div key={l} className="bg-[#0a1424] px-6 py-6">
                  <div className="font-serif text-3xl text-teal-300 mb-1 tabular-nums">{n}</div>
                  <div className="text-[11px] tracking-[0.14em] uppercase text-white/50 font-semibold">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pipeline */}
      <section className="border-t border-white/10 bg-[#0a1220]" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-amber-300 mb-4">The decision pipeline</div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-10 max-w-2xl">
              Beyond summaries. <span className="italic text-teal-300">A strategic decision-support system.</span>
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-y-4 items-center">
            {PIPELINE.map((step, i) => (
              <Reveal key={step} delay={i * 60} className="flex items-center">
                <span className="text-[12px] sm:text-[13px] text-white/75 border border-white/15 rounded-full px-4 py-2 bg-white/[0.03]">
                  <span className="text-teal-300 font-bold mr-1.5">{i + 1}</span>
                  {step}
                </span>
                {i < PIPELINE.length - 1 && <ArrowRight className="w-4 h-4 text-white/25 mx-2" />}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Dimensions */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-teal-300 mb-4">The enterprise value scorecard</div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-3 max-w-2xl">
              Ten dimensions. <span className="italic text-amber-300">One transparent 0–100 score.</span>
            </h2>
            <p className="text-white/55 max-w-2xl mb-10">
              Default weights adapt by industry while always totalling 100%. Every dimension carries a RAG status,
              trend, peer percentile, confidence rating and evidence-coverage percentage.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {DIMENSIONS.map(({ Icon, name, w }, i) => (
              <Reveal key={name} delay={i * 50}>
                <div className="rounded-xl border border-white/10 bg-[#0c1626] p-5 h-full hover:border-teal-400/40 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="w-4.5 h-4.5 w-5 h-5 text-teal-300" strokeWidth={1.75} />
                    <span className="font-serif text-amber-300 text-lg tabular-nums">{w}</span>
                  </div>
                  <div className="text-[13px] font-semibold text-white/85 leading-snug">{name}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Agents */}
      <section className="border-t border-white/10 bg-[#0a1220]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-amber-300 mb-4">Agentic analysis architecture</div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-4 max-w-3xl">
              Fifteen specialists. <span className="italic text-teal-300">One orchestrated judgement.</span>
            </h2>
            <p className="text-sm text-white/50 max-w-3xl mb-10" data-testid="ci-model-fleet-note">
              Every agent runs on the latest frontier models — OpenAI GPT-6 Astra, Anthropic Claude Fable 5.1 and Google Gemini 3.8 Flash — with automatic cross-provider failover.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7">
            {AGENTS.map(([name, desc], i) => (
              <Reveal key={name} delay={i * 40}>
                <div className="border-t border-white/12 pt-4">
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="font-serif text-white/30 text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[14px] font-semibold text-white">{name} Agent</span>
                  </div>
                  <p className="text-[13px] text-white/55 leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Guardrails */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-5 h-5 text-teal-300" />
              <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-teal-300">Evidence &amp; source controls</div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl mb-10 max-w-3xl">
              Not another AI summary tool. <span className="italic text-amber-300">A trusted evidence system.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {GUARDRAILS.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 50}>
                <div className="rounded-xl border border-white/10 bg-[#0c1626] p-6 h-full">
                  <div className="text-[13px] font-bold text-white mb-2">{title}</div>
                  <p className="text-[13px] text-white/55 leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverable + CTA */}
      <section className="border-t border-white/10 bg-[#0a1220]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-amber-300 mb-4">The deliverable</div>
              <h2 className="font-serif text-3xl sm:text-4xl mb-5">
                A 21-section, <span className="italic text-teal-300">board-ready</span> value-creation report.
              </h2>
              <ul className="space-y-2.5 text-[14px] text-white/65">
                {[
                  "Executive summary with one-line board verdict & evidence coverage",
                  "Enterprise value scorecard — weight × score, fully reproducible",
                  "Peer matrix, SWOT, Five Forces & moat assessment",
                  "Board risk register with residual exposure & accountable owners",
                  "Quantified value pools — conservative / base / stretch scenarios",
                  "Independent Director's notes per dimension",
                  "36-month transformation roadmap & benefit-tracking register",
                  "Consolidated Sources & Evidence Register with confidence ratings",
                ].map((x) => (
                  <li key={x} className="flex gap-3">
                    <span className="w-1 h-1 rounded-full bg-teal-300 mt-2 shrink-0" />
                    {x}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-2xl border border-white/12 bg-[#0c1626] p-8">
                <div className="flex items-center gap-3 mb-5">
                  <FileSearch className="w-5 h-5 text-amber-300" />
                  <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-white/60">Start in minutes</div>
                </div>
                <ol className="space-y-4 text-[14px] text-white/70 mb-8">
                  {[
                    "Identify the company — 14 profile fields, purpose & audience",
                    "Upload annual reports, filings, presentations & prior AI reports",
                    "Approve the AI-recommended peer set across 4 benchmark groups",
                    "Configure weights, depth, perspective & scenario stance",
                    "Launch the 15-agent analysis and export the board pack",
                  ].map((x, i) => (
                    <li key={x} className="flex gap-3">
                      <span className="font-serif text-teal-300 tabular-nums shrink-0">{i + 1}.</span>
                      {x}
                    </li>
                  ))}
                </ol>
                <Link
                  to="/company-intelligence/dashboard"
                  data-testid="ci-landing-enter-bottom-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal-400 hover:bg-teal-300 text-[#06251f] font-bold text-sm px-7 py-3.5 transition-colors"
                >
                  Enter the Intelligence Suite <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="flex items-center justify-center gap-2 mt-4 text-[11px] text-white/35">
                  <Scale className="w-3.5 h-3.5" /> Restricted preview · SAMANVAI administration access required
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/35">
          <div className="flex items-center gap-2">
            <Network className="w-3.5 h-3.5" /> SAMANVAI Company Intelligence · Verified evidence → comparable metrics → transparent scores → board-level action
          </div>
          <div>Strictly confidential deliverables · Prepared by SAMANVAI</div>
        </div>
      </footer>
    </div>
  );
}
